<script setup lang="ts">
import { VtkViewContext } from '@/src/components/vtk/context';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import { useMouseRangeManipulatorListener } from '@/src/core/vtk/useMouseRangeManipulatorListener';
import { useVtkInteractionManipulator } from '@/src/core/vtk/useVtkInteractionManipulator';
import { Maybe } from '@/src/types';
import { LPSAxisDir } from '@/src/types/lps';
import vtkGatedMouseRangeManipulator from '@/src/vtk/GatedMouseRangeManipulator';
import { IMouseRangeManipulatorInitialValues } from '@kitware/vtk.js/Interaction/Manipulators/MouseRangeManipulator';
import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator';
import { syncRef } from '@vueuse/core';
import { inject, toRefs, unref, watch, computed } from 'vue';
import { useViewStore } from '@/src/store/views';
import { useActionHeld } from '@/src/composables/useKeyboardShortcuts';

type Props = {
  viewId: string;
  imageId: Maybe<string>;
  viewDirection: LPSAxisDir;
  manipulatorConfig?: IMouseRangeManipulatorInitialValues;
};

const props = defineProps<Props>();
const { viewId, imageId, manipulatorConfig } = toRefs(props);

const view = inject(VtkViewContext);
if (!view) throw new Error('No VtkView');

const interactorStyle =
  view.interactorStyle as Maybe<vtkInteractorStyleManipulator>;
if (!interactorStyle?.isA('vtkInteractorStyleManipulator')) {
  throw new Error('No vtkInteractorStyleManipulator');
}

const config = computed(() => {
  return {
    button: -1,
    dragEnabled: false,
    scrollEnabled: false,
    ...manipulatorConfig?.value,
  };
});

const { instance: rangeManipulator } = useVtkInteractionManipulator(
  interactorStyle,
  vtkGatedMouseRangeManipulator,
  config
);

rangeManipulator.value.setupMouseMove(view.interactor);

const enableGrabSlice = useActionHeld('grabSlice');
watch(
  enableGrabSlice,
  (value) => {
    rangeManipulator.value.setGateEnabled(value);
  },
  { immediate: true }
);

const sliceConfig = useSliceConfig(viewId, imageId);

const scroll = useMouseRangeManipulatorListener(
  rangeManipulator,
  'vertical',
  sliceConfig.range,
  1,
  sliceConfig.slice.value,
  -1,
  // Set the scrolled view as the active view — only on real user input.
  () => {
    useViewStore().setActiveView(unref(viewId));
  }
);

syncRef(scroll, sliceConfig.slice, { immediate: true });
</script>

<template><slot></slot></template>
